import { select, call, SelectEffect, CallEffect } from 'redux-saga/effects';
import cloneDeep from 'lodash.clonedeep';

import { ElementClipboardPayload, ExistingScribeModel, VSElementModel } from 'js/types';
import { getActiveElements, getScribeById } from 'js/sagas/selectors';
import { EditorCopyAction } from 'js/actionCreators/editorActions';

import createSceneClipboardPayload from '../sagaHelpers/createSceneClipboardPayload';
import { writeToClipboard } from '../sagaHelpers/writeToClipboard';

export function* copy({
  scribeId,
  asCut = false
}: EditorCopyAction): Generator<SelectEffect | CallEffect, void, (ExistingScribeModel | undefined) | string[]> {
  const scribe = (yield select(getScribeById, scribeId)) as ExistingScribeModel | undefined;

  if (!scribe) return;

  const activeScene = scribe.scenes.find(scene => scene.active);
  const activeElementIds = cloneDeep((yield select(getActiveElements)) as string[]);
  const selectedSceneIds = (yield select(state => state.scribes.selectedSceneIds)) as string[];

  const hasScenesSelected = !!selectedSceneIds.length;
  const hasElementsSelected = !!activeElementIds.length;

  if (!hasScenesSelected && !hasElementsSelected) return;

  if (hasScenesSelected) {
    const clipboardPayload = createSceneClipboardPayload(scribe, selectedSceneIds);
    yield call(writeToClipboard, clipboardPayload);
  } else {
    if (!activeScene) return;

    const activeSceneElementIds = activeScene.elementIds;

    activeElementIds.sort((a, b) => activeSceneElementIds.indexOf(a) - activeSceneElementIds.indexOf(b));

    const elementClones: (Partial<VSElementModel> | null)[] = activeElementIds.map(elementId => {
      const clone = cloneDeep(scribe.elements.find(el => el.id === elementId)) as Partial<VSElementModel>;
      if (!clone) return null;

      delete clone.id;
      return clone;
    });

    const clipboardPayload: ElementClipboardPayload = {
      type: 'element',
      metadata: {
        sceneId: activeScene.id,
        wasCut: asCut
      },
      data: elementClones.filter(clone => clone !== null)
    };

    yield call(writeToClipboard, clipboardPayload);
  }
}
